import {
	Dialog as MuiDialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Button,
	Box,
	FormControlLabel,
	Switch,
	FormHelperText,
	InputLabel,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDialog } from '../redux/slices/dialog';
import { useRedux } from '../redux';
import { useIsMobile } from '../hooks';
import { I18NKey, useI18N } from '../i18n';

export interface CreateGroupDialogErrors {
	name: I18NKey;
	emailNotification: I18NKey;
	isFranchise: I18NKey;
	phoneNumbers: I18NKey;
}

const CreateGroupDialog = (): JSX.Element => {
	const i18n = useI18N();
	const isMobile = useIsMobile();
	const { dialog, snackbar } = useRedux();
	const { isOpen, action, data } = useDialog();

	const [name, setName] = useState(data?.name || '');
	const [emailNotification, setEmailNotification] = useState(data?.emailNotification || '');
	const [isFranchise, setIsFranchise] = useState(data?.isFranchise ?? false);
	const [phoneNumbers, setPhoneNumbers] = useState(() => {
		if (Array.isArray(data?.phoneNumbers)) {
			return data.phoneNumbers
				.map((n) => n.replace(/^\+55/, '')) // remove prefixo +55
				.join(', ');
		}
		return '';
	});

	const [errors, setErrors] = useState<CreateGroupDialogErrors>({
		name: '',
		emailNotification: '',
		isFranchise: '',
		phoneNumbers: '',
	});

	const handleCancel = () => {
		dialog.update({ data: {} });
		dialog.hide();
	};

	const singlePhoneRegex = /^\d{11}$/;

	const parseAndValidatePhones = (raw: string) => {
		const numbers = raw
			.split(',')
			.map((n) => n.trim())
			.filter((n) => !!n);

		const invalids = numbers.filter((n) => !singlePhoneRegex.test(n));

		return { numbersValid: invalids.length === 0, numbers, invalids };
	};

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setPhoneNumbers(value);

		const { numbersValid } = parseAndValidatePhones(value);

		setErrors((prev) => ({
			...prev,
			phoneNumbers: numbersValid ? '' : (`dialogs.${action}.errors.phone.invalid` as I18NKey),
		}));
	};

	const handleCreate = () => {
		const newErrors: CreateGroupDialogErrors = {
			name: '',
			emailNotification: '',
			isFranchise: '',
			phoneNumbers: '',
		};
		let hasErrors = false;

		if (!name) {
			newErrors.name = `dialogs.${action}.errors.name.empty` as I18NKey;
			hasErrors = true;
		}

		if (!emailNotification) {
			newErrors.emailNotification = `dialogs.${action}.errors.email.empty` as I18NKey;
			hasErrors = true;
		}

		const { numbersValid, numbers } = parseAndValidatePhones(phoneNumbers);

		if (phoneNumbers.trim() && !numbersValid) {
			newErrors.phoneNumbers = `dialogs.${action}.errors.phones.invalid` as I18NKey;
			hasErrors = true;
		}

		if (!action) {
			snackbar.show({ status: 'error', message: 'dialog.errors.ok' });

			return;
		}

		if (hasErrors) {
			setErrors(newErrors);

			return;
		}

		dialog.update({
			data: {
				name,
				emailNotification,
				isFranchise,
				id: data.id,
				phoneNumbers: numbers,
			},
		});

		dialog.hide();
	};

	if (action !== 'create-group' && action !== 'update-group') {
		return <></>;
	}

	return (
		<MuiDialog open={isOpen}>
			<DialogTitle>
				{action === 'create-group'
					? i18n(`dialogs.${action}.createGroup` as I18NKey)
					: i18n(`dialogs.${action}.edit` as I18NKey)}
			</DialogTitle>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					gap: 2,
					minWidth: isMobile ? 200 : 400,
					py: 2,

					'> *': {
						width: 1,
					},
				}}
			>
				<DialogContent>
					<InputLabel>{i18n(`dialogs.${action}.name` as I18NKey)}</InputLabel>
					<TextField
						label='Ex: "John Doe"'
						value={name}
						onChange={(e) => setName(e.target.value)}
						fullWidth
						error={!!errors.name}
						helperText={errors.name ? i18n(errors.name) : ''}
					/>
				</DialogContent>

				<DialogContent>
					<InputLabel>{i18n(`dialogs.${action}.email` as I18NKey)}</InputLabel>
					<TextField
						label="Ex: 2yOJ2@example.com"
						value={emailNotification}
						onChange={(e) => setEmailNotification(e.target.value)}
						fullWidth
						error={!!errors.emailNotification}
						helperText={errors.emailNotification ? i18n(errors.emailNotification) : ''}
					/>
				</DialogContent>
				<DialogContent>
					<InputLabel>{i18n(`dialogs.${action}.phones` as I18NKey)}</InputLabel>
					<TextField
						label="Ex: DDXXXXXXXX, DDXXXXXXXX, DDXXXXXXXX"
						value={phoneNumbers}
						onChange={handlePhoneChange}
						fullWidth
						error={!!errors.phoneNumbers}
						helperText={errors.phoneNumbers ? i18n(errors.phoneNumbers) : ''}
					/>
				</DialogContent>
				<DialogContent>
					<FormControlLabel
						control={
							<Switch checked={!!isFranchise} onChange={(e) => setIsFranchise(e.target.checked)} />
						}
						label={i18n(`dialogs.${action}.franchise` as I18NKey)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancel}>{i18n(`dialog.cancel`)}</Button>
					<Button onClick={handleCreate} variant="contained">
						{action === 'create-group'
							? i18n(`dialogs.create` as I18NKey)
							: i18n(`dialogs.update` as I18NKey)}
					</Button>
				</DialogActions>
			</Box>
		</MuiDialog>
	);
};

export default CreateGroupDialog;
